<template>
  <div class="filter-component">
    <v-card class="mb-4" outlined>
      <v-card-text>
        <div class="d-flex align-center mb-2">
          <h3 class="text-subtitle-1 font-weight-medium mr-2">Filters</h3>
          <v-spacer></v-spacer>
          <v-btn
            v-if="localFilters.length > 0"
            text
            color="primary"
            x-small
            @click="resetFilters"
          >
            Clear all
          </v-btn>
        </div>

        <div
          v-for="(condition, index) in localFilters"
          :key="index"
          class="mb-2"
        >
          <filter-condition
            :condition="condition"
            :fields="fields"
            :used-fields="getUsedFieldsExcept(index)"
            @update:condition="updateCondition(index, $event)"
            @remove="removeCondition(index)"
          />
        </div>

        <div class="d-flex align-center mt-3">
          <v-btn
            color="primary"
            text
            @click="addCondition"
            class="px-0"
            small
            :disabled="areAllFieldsUsed()"
          >
            <v-icon small left>mdi-plus</v-icon>
            Add filter
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="localFilters.length > 0"
            color="primary"
            small
            @click="applyFilters"
            :disabled="localFilters.length === 0"
          >
            Apply
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FilterCondition from "./FilterCondition.vue";
import { FILTER_TYPES } from "../../../consts/filterTypes";

export default {
  name: "FilterComponent",
  components: {
    FilterCondition,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localFilters: [],
    };
  },
  created() {
    // Initialize with any provided filters
    if (this.value && this.value.length > 0) {
      this.localFilters = JSON.parse(JSON.stringify(this.value));
    }
  },
  methods: {
    // Get all fields that are already used in other conditions except the one at the given index
    getUsedFieldsExcept(excludeIndex) {
      return this.localFilters
        .filter((_, index) => index !== excludeIndex)
        .map((filter) => filter.field);
    },

    // Find the first available field that isn't already used
    getFirstAvailableField() {
      const usedFields = this.localFilters.map((filter) => filter.field);
      return this.fields.find((field) => !usedFields.includes(field.value));
    },

    // Check if all fields are already used
    areAllFieldsUsed() {
      const usedFields = this.localFilters.map((filter) => filter.field);
      return usedFields.length >= this.fields.length;
    },

    addCondition() {
      // Check if all fields are already used
      if (this.areAllFieldsUsed()) {
        return;
      }

      const defaultField = this.getFirstAvailableField();
      if (!defaultField) {
        return;
      }

      const fieldType = defaultField.type || "TEXT";
      this.localFilters.push({
        field: defaultField.value,
        operator: FILTER_TYPES[fieldType].operators[0].value,
        value: null,
        value2: null,
      });
    },
    updateCondition(index, condition) {
      const oldField = this.localFilters[index].field;
      const newField = condition.field;

      // If the field has changed and the new field is already used in another filter
      if (oldField !== newField) {
        // Find any other filter that uses the same field
        const conflictIndex = this.localFilters.findIndex(
          (filter, i) => i !== index && filter.field === newField
        );

        // If there's a conflict, remove the conflicting filter
        if (conflictIndex !== -1) {
          this.localFilters.splice(conflictIndex, 1);
        }
      }

      // Update the filter condition
      this.localFilters[index] = { ...condition };
    },
    removeCondition(index) {
      this.localFilters.splice(index, 1);

      // If this was the last filter, reset filters (same as "clear all")
      if (this.localFilters.length === 0) {
        this.resetFilters();
      }
    },
    applyFilters() {
      // Filter out any conditions with null values before emitting
      const validFilters = this.localFilters.filter(
        (filter) => filter.value !== null
      );
      this.$emit("input", validFilters);
      this.$emit("apply", validFilters);
    },
    resetFilters() {
      this.localFilters = [];
      this.$emit("input", []);
      this.$emit("reset");
    },
    getFilterLabel(filter) {
      const field = this.fields.find((f) => f.value === filter.field);
      if (!field) return "";

      const fieldType = field.type || "TEXT";
      const operatorType = FILTER_TYPES[fieldType];
      if (!operatorType) return "";

      const operator = operatorType.operators.find(
        (o) => o.value === filter.operator
      );
      if (!operator) return "";

      let valueLabel = filter.value;
      if (field.type === "SELECT" && field.options) {
        const option = field.options.find((o) => o.value === filter.value);
        valueLabel = option ? option.label : filter.value;
      }

      if (filter.operator === "between" || filter.operator === "not_between") {
        return `${field.label} ${operator.label} ${filter.value} and ${filter.value2}`;
      }

      return `${field.label} ${operator.label} ${valueLabel}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.filter-component {
  .v-chip {
    background-color: var(--v-primary-lighten5);
    color: var(--v-primary-base);
  }
}
</style>